// MapComponent.js
import 'leaflet/dist/leaflet.css'
import './drilldownSiteLocationsMap.css'

import { Spin } from 'antd'
import {
  DrilldownDataContext,
  useCountHistograms,
  useFilterToSites,
  useVersionAwareDrilldownPanelData,
} from 'features/drilldown'
import L, { Icon } from 'leaflet'
import markerIconPng from 'leaflet/dist/images/marker-icon.png'
import _ from 'lodash'
import React, {
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet'
import { GeckoSite, GenericPanelData } from 'types'

import {
  LeafletAreaSelect,
  SelectablePoint,
  SerializableLatLngBounds,
} from './LeafletAreaSelect'
import { MapBounds } from './MapBounds'

interface MapPanelData extends GenericPanelData {
  selectedMapArea: SerializableLatLngBounds | null
}

const LATEST_DATA_SCHEMA_VERSION = 3

export const DEFAULT_MAP_PANEL_DATA: MapPanelData = {
  panelDataSchemaVersion: LATEST_DATA_SCHEMA_VERSION,
  selectedMapArea: null,
  title: 'Site Map',
  isPinned: true,
}

export interface DrilldownSiteLocationsMapProps {
  mapStyle?: React.CSSProperties
  onHoverSiteMarker?: (siteId: string) => void
  onClickSiteMarker?: (siteId: string) => void
}

/**
 * A Drilldown component to show locations on a map, and allow a user to
 * select an area, adding the selected points to the context as a filter.
 * @param props
 * @returns component
 */
const MapComponent: FC<DrilldownSiteLocationsMapProps> = ({
  mapStyle,
  onHoverSiteMarker,
  onClickSiteMarker,
}) => {
  const { sites: allSites, isLoading } = useContext(DrilldownDataContext)
  const [selectedPoints, setSelectedPoints] = useState<ReadonlyArray<string>>(
    []
  )

  const { panelData, setPanelData } =
    useVersionAwareDrilldownPanelData<MapPanelData>()

  const {
    histograms: [histogram],
  } = useCountHistograms({
    groupingType: 'site',
    aggregationType: 'unit',
  })

  const { selectedMapArea } = panelData

  const sites = useMemo<readonly GeckoSite[]>(() => {
    const filteredSiteIds = new Set(Object.keys(histogram))
    return allSites.filter((s) => filteredSiteIds.has(s.id))
  }, [allSites, histogram])

  const _selectedPointsAsSet = useMemo(
    () => new Set(selectedPoints),
    [selectedPoints]
  )

  useFilterToSites(_selectedPointsAsSet)
  const siteLocations: readonly SelectablePoint[] = useMemo(
    () =>
      sites
        .map((site) => {
          const coordinates = site.data.coordinates as
            | [number, number]
            | undefined
          return coordinates
            ? {
                id: site.id,
                lat: coordinates[0],
                lng: coordinates[1],
              }
            : null
        })
        .filter((point): point is SelectablePoint => !!point),
    [sites]
  )

  const defaultMapStyle = useMemo(
    () => ({
      height: '500px',
      width: '100%',
      ...(mapStyle ?? {}),
    }),
    [mapStyle]
  )

  const setSelectedMapArea = useCallback(
    (val: SetStateAction<SerializableLatLngBounds | null>) => {
      setPanelData((prev) => {
        const newVal =
          typeof val === 'function' ? val(prev.selectedMapArea) : val
        if (_.isEqual(newVal, prev.selectedMapArea)) {
          return prev
        }
        return {
          ...prev,
          selectedMapArea: newVal,
        }
      })
    },
    [setPanelData]
  )

  const pointInBounds = useMemo(() => {
    if (!selectedMapArea) return () => true
    const bounds = L.latLngBounds(
      selectedMapArea.southWest,
      selectedMapArea.northEast
    )
    return ([lat, lng]: [number, number]) => bounds.contains([lat, lng])
  }, [selectedMapArea])

  useEffect(() => {
    if (siteLocations && siteLocations.length && setSelectedPoints) {
      const selectedPointIds = siteLocations
        .filter(({ lat, lng }) => pointInBounds([lat, lng]))
        .map(({ id }) => id)

      setSelectedPoints((prev) =>
        _.isEqual(selectedPointIds, prev) ? prev : selectedPointIds
      )
    }
  }, [pointInBounds, siteLocations])

  return (
    <Spin spinning={isLoading} wrapperClassName='site-map-spin-wrapper'>
      <MapContainer center={[0, 0]} style={defaultMapStyle}>
        <TileLayer url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
        <LeafletAreaSelect
          selectedMapArea={selectedMapArea}
          setSelectedMapArea={setSelectedMapArea}
        />
        {siteLocations.map(({ id, lat, lng }) => {
          const site = _.find(sites, (s) => s.id === id)
          return (
            !!site && (
              <Marker
                key={id}
                position={{ lat, lng }}
                icon={
                  new Icon({
                    iconUrl: markerIconPng,
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                  })
                }
                eventHandlers={{
                  mouseover: () =>
                    onHoverSiteMarker
                      ? () => onHoverSiteMarker(site.id)
                      : () => {},
                  click: onClickSiteMarker
                    ? () => onClickSiteMarker(site.id)
                    : () => {},
                }}>
                <Popup>{site.data.name}</Popup>
              </Marker>
            )
          )
        })}
        <MapBounds
          locations={siteLocations.map((location) => [
            location.lat,
            location.lng,
          ])}
        />
      </MapContainer>
    </Spin>
  )
}

export const DrilldownSiteLocationsMap = React.memo(MapComponent)
